
import { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { InventoryItem } from '@/types/inventory';
import { searchInventoryOptimized } from '@/services/search/optimizedInventorySearch';
import { toast } from 'sonner';

export type SearchDependencies = {
  lastUpdate: string | null;
  setLastUpdate: (value: string | null) => void;
};

export function useInventorySearch(dependencies: SearchDependencies) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<InventoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const { lastUpdate, setLastUpdate } = dependencies;
  
  // Use 300ms debounce for optimized search responsiveness
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Enhanced search function with input validation
  const performSearch = useCallback(async (term: string, warehouseId: number | null = null) => {
    // Clean and validate input
    const cleanTerm = term.trim();
    
    // Don't search if term is too short and no warehouse filter
    if (cleanTerm.length < 2 && warehouseId === null) {
      setResults([]);
      setHasSearched(false);
      return [];
    }

    setIsLoading(true);
    setHasSearched(true);
    
    try {
      console.log(`🚀 Enhanced search: "${cleanTerm}" ${warehouseId ? `in magazijn ${warehouseId}` : ''}`);
      
      const { results: searchResults, lastUpdate: timestamp } = await searchInventoryOptimized(cleanTerm, warehouseId);
      
      // Update last update timestamp if available
      if (timestamp) {
        setLastUpdate(timestamp);
      }
      
      // Update results
      setResults(searchResults);

      // Enhanced user feedback with improved messaging
      if (searchResults.length === 0) {
        if (cleanTerm.length < 2) {
          toast.info('Voer minimaal 2 karakters in om te zoeken');
        } else if (warehouseId !== null) {
          toast.warning(`Geen producten gevonden voor "${cleanTerm}" in magazijn ${warehouseId}`);
        } else {
          toast.warning(`Geen producten gevonden voor "${cleanTerm}"`);
        }
      } else {
        const warehouseText = warehouseId !== null ? ` in magazijn ${warehouseId}` : '';
        toast.success(`${searchResults.length} relevante product${searchResults.length === 1 ? '' : 'en'} gevonden${warehouseText}`);
      }
      
      return searchResults;
      
    } catch (error) {
      console.error('Optimized search error:', error);
      toast.error('Er is een fout opgetreden bij het zoeken. Probeer het opnieuw.');
      setResults([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [setLastUpdate]);

  // Clear search results
  const clearResults = useCallback(() => {
    setResults([]);
    setHasSearched(false);
  }, []);

  return {
    // State
    searchTerm,
    setSearchTerm,
    results,
    setResults,
    isLoading,
    hasSearched,
    debouncedSearch,
    lastUpdate,
    
    // Actions
    performSearch,
    clearResults
  };
}
